import { Button } from "@/components/ui/button"
import { SheetPageBody, SheetPageTitle } from "@/components/ui/sheet-page"
import { VALIDATION_ERROR_KEYS } from "@/i18n"
import { MailPlus } from "lucide-react"
import { useTranslation } from "react-i18next"
import * as z from "zod"
import { useNewProps } from "./page-props"
import { zodResolver } from "@hookform/resolvers/zod"
import { useController, useForm } from "react-hook-form"
import { FormField } from "@/components/ui/form-field"
import { Combobox } from "@/components/ui/combobox"
import { CommandItem } from "@/components/ui/command"
import { Textarea } from "@/components/ui/textarea"
import { useNavigate } from "@/lib/page-rendering"

const validator = z.object({
  listing_profile_uuid: z
    .string(VALIDATION_ERROR_KEYS.REQUIRED)
    .min(1, VALIDATION_ERROR_KEYS.REQUIRED),
  message: z.string().min(1, VALIDATION_ERROR_KEYS.REQUIRED),
})

type FormSchema = z.infer<typeof validator>

export default function Page() {
  const navigate = useNavigate()
  const { t } = useTranslation("school_relief_listings", {
    keyPrefix: "applications",
  })
  const {
    relief_listing_title,
    school_name,
    learners_relief_listing_id,
    listing_profile_options,
  } = useNewProps()

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validator),
    defaultValues: { message: t("form.message_pro_forma") },
  })

  const { field } = useController({ name: "listing_profile_uuid", control })

  const onSubmit = (data: FormSchema) => {
    const { message, listing_profile_uuid } = data

    navigate({
      href: ".",
      method: "post",
      data: {
        application: {
          listing_profile_uuid,
          learners_relief_listing_id,
        },
        message,
      },
    })
  }
  return (
    <SheetPageBody>
      <SheetPageTitle icon={MailPlus}>
        {t("title", { title: relief_listing_title, school_name })}
      </SheetPageTitle>
      <p className="mb-6">{t("new.description")}</p>
      <form
        role="form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full flex-col gap-5"
      >
        <FormField
          control={control}
          name={"listing_profile_uuid"}
          label={t("form.listing_profile")}
          errors={errors}
        >
          <Combobox
            value={field.value}
            onChange={(value) => field.onChange(value)}
            options={listing_profile_options}
            renderComboboxItem={({ key, setOpen, option }) => (
              <CommandItem
                className="flex-col items-start gap-0"
                key={option.value}
                disabled={option.disabled}
                keywords={[key, option.value?.toString() ?? ""]}
                aria-label={option.label}
                onSelect={() => {
                  if (field.value === option.value) {
                    field.onChange(null)
                  } else {
                    field.onChange(option.value)
                  }
                  setOpen(false)
                }}
              >
                <span>{option.label}</span>
                {option.disabled && (
                  <span className="text-xs">{t("form.disabled_listing")}</span>
                )}
              </CommandItem>
            )}
          />
        </FormField>
        <FormField
          {...register("message")}
          label={t("form.message")}
          errors={errors}
        >
          <Textarea />
        </FormField>
        <Button type="submit" className="mt-4 w-full">
          {t("send")}
        </Button>
      </form>
    </SheetPageBody>
  )
}
